import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Site } from "../../../lib/src/api/partner/models/Site";
import { useApiClient } from "@hostingbv/lib/src/api";
import { useFetchedResource } from "@hostingbv/lib/src/util/useFetchedResource";
import { Button } from "@hostingbv/lib/src/components/form/Button";
import { faAdd, faBolt, faChainSlash, faChevronRight, faCode, faCog, faExternalLinkAlt, faGlobe, faHeartCircleCheck, faInfoCircle, faKey, faPencil, faPlaneDeparture, faRocket, faServer, faTachometer, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { config } from "../config";
import { usePermissions } from "@hostingbv/lib/src/auth/permissions";
import { useModal } from "@hostingbv/lib/src/components/layout/ModalProvider";
import { EditSiteNginxConfigModal } from "../modals/EditSiteNginxConfigModal";
import { EditSitePhpConfigModal } from "../modals/EditSitePhpConfigModal";
import { DeleteSiteModal } from "../modals/DeleteSiteModal";
import { Card } from "@hostingbv/lib/src/components/Card";
import { HoverHint } from "@hostingbv/lib/src/components/content/HoverHint";
import { SshKey } from "@hostingbv/lib/src/api/partner/models/SshKey";
import { AddDeploymentKeyModal } from "../modals/AddDeploymentKeyModal";
import { Paragraph } from "@hostingbv/lib/src/components/Paragraph";
import { NavLink } from "react-router-dom";
import { FailedDeploymentBanner } from "./FailedDeploymentBanner";
import { PlannedRemovalBanner } from "./PlannedRemovalBanner";
import { InDevelopment } from "@hostingbv/lib/src/components/InDevelopment";
import { EditPhpVersionModal } from "../modals/EditPhpVersionModal";
import { MoveToCustomerModal } from "../modals/MoveToCustomerModal";
import { Copyable } from "@hostingbv/lib/src/components/content/Copyable";
import { useRefresh } from "@hostingbv/lib/src/components/RefreshController";
import { useRealtimeDeployments } from "./deployments/RealtimeDeployments";

export const PleskSiteDetails: FC<{ site: Site }> = ({ site }) => {
  return <>
    <FailedDeploymentBanner entityOrDeployment={site} />
    <PlannedRemovalBanner site={site} />
    <SiteInfoCard site={site} />
    <section><AdvancedSiteSettings site={site} /></section>
    {site?.sshKeys && <SshKeyCard site={site} keys={site.sshKeys} />}
    {
      site.server && <section>
        <div className={"relative"}>
          <MonitoringCard />
          <div className={"absolute top-0 left-0 h-full w-full bg-slate-50 opacity-75 flex flex-col space-y-8 items-center justify-center"}>
            <span className={"font-medium text-slate-600"}>Coming soon!</span>
            <InDevelopment />
          </div>
        </div>
      </section>
    }
  </>
}

const SiteInfoCard: FC<{ site: Site }> = ({ site }) => {
  const { partnerClient } = useApiClient(config)
  const { can } = usePermissions(partnerClient)
  const { resource: domains, reload: reloadDomains } = useFetchedResource(() => partnerClient.getAllDomains())
  useEffect(() => { reloadDomains() }, [])
  const { resource: phpVersions, reload: reloadPhpVersions } = useFetchedResource(() => partnerClient.getAvailablePhpVersions(site.serverId))
  useEffect(() => { reloadPhpVersions() }, [])
  const refresh = useRefresh()
  const { awaitNextDeploymentFinished } = useRealtimeDeployments()!

  const savePhpVersion = async (version: string) => {
    const deployment = await partnerClient.setSitePhpVersion(site.id, version)
    if (deployment.success) {
      await awaitNextDeploymentFinished(deployment.data.id)
    }
    refresh()
  }

  const domain = useMemo(() => {
    const allDomains = domains?.success ? domains.data : []
    const exactMatch = allDomains.find((domain) => {
      return domain.displayName === site.displayName
    })
    if (exactMatch) {
      return exactMatch
    }
    const partialMatch = allDomains.find((domain) => {
      return site.displayName.endsWith('.' + domain.displayName)
    })
    if (partialMatch) {
      return partialMatch
    }
    return null
  }, [domains, site])

  const manage = async () => {
    const url = await partnerClient.getSiteManageUrl(site.id)
    window.open(url, "_blank")
  }

  const { open: openPHPVersionModal } = useModal({ title: "Change PHP version", body: <EditPhpVersionModal active={site.deploymentDetails?.phpHandler ?? null} availableVersions={phpVersions ?? {}} save={savePhpVersion} /> })
  const { open: openCustomerModal } = useModal({ title: "Move to customer", body: <MoveToCustomerModal currentCustomer={site.customer!} save={c => partnerClient.setSiteCustomer(site.id, c.id)} /> })

  const currentPhpVersion = useMemo(() => {
    if (site.deploymentDetails === null || phpVersions === null) {
      return null
    }
    return phpVersions[site.deploymentDetails.phpHandler]
  }, [phpVersions, site.deploymentDetails])

  return <>
    {can('edit', 'sites', site.permissions) && <OptimizeOptions site={site} />}
    <Card title={'Site details'}>
      <div className={"flex items-stretch"}>
        <div className={"flex-1"}>
          <dl>
            <dt className={"text-slate-500 text-xs"}>Address</dt>
            <dd className={"text-slate-900 mb-4"}><a href={`https://${site.displayName}`} target={"_blank"} className={"text-brand-500 underline"}>{site.displayName} <FontAwesomeIcon icon={faExternalLinkAlt} className={"text-xs inline align-baseline"} /></a></dd>
            {site.deploymentDetails && <>
              <dt className={"text-slate-500 text-xs"}>IP Address</dt>
              <dd className={"text-slate-900 mb-4"}>
                <div className={"flex flex-wrap space-x-2 mt-1"}>
                  {site.deploymentDetails.ipAddresses.map((ip, i) => <Copyable key={i} text={ip} />)}
                </div>
              </dd>
            </>}
            {currentPhpVersion && <>
              <dt className={"text-slate-500 text-xs"}>PHP Version</dt>
              <dd className={"text-slate-900 mb-4"}>
                <div className={"flex items-center space-x-3"}>
                  <span>PHP {currentPhpVersion}</span>
                  {can('edit', 'sites', site.permissions) && <button className={"h-6 w-6 border border-slate-200 rounded-sm text-slate-500 hover:bg-slate-50 text-xs"} onClick={() => openPHPVersionModal()}>
                    <FontAwesomeIcon icon={faPencil} />
                  </button>}
                </div>
              </dd>
              {site.sshUserName && site.deploymentDetails && <>
                <dt className={"text-slate-500 text-xs"}>SSH Login</dt>
                <dd className={"text-slate-900 mb-4"}><Copyable text={site.sshUserName + "@" + site.deploymentDetails.ipAddresses[0]} /></dd>
              </>}
            </>}
            {site.customer && <>
              <dt className={"text-slate-500 text-xs"}>Customer</dt>
              <dd className={"text-slate-900 mb-4"}>
                <div className={"flex items-center space-x-3"}>
                  <span>{site.customer?.displayName}</span>
                  {can('edit', 'sites', site.permissions) && <button className={"h-6 w-6 border border-slate-200 rounded-sm text-slate-500 hover:bg-slate-50 text-xs"} onClick={openCustomerModal}>
                    <FontAwesomeIcon icon={faPencil} />
                  </button>}
                </div>
              </dd>
            </>}
          </dl>
        </div>
        {can('edit', 'sites', site.permissions) && <div className={"flex flex-col items-end space-y-4"}>
          <Button type={"primary"} size={"sm"} text={"Manage in Plesk"} icon={faExternalLinkAlt} onClick={manage} />
          <form action="https://internet.nl/site/" target={"_blank"} method="POST">
            <input id="internetnl-site-url" type="hidden" name="url" value={site.displayName} />
            <Button icon={faTachometer} size={"sm"} type={"secondary"} submit={true} text={"Run internet.nl benchmark"} />
          </form>
        </div>}
      </div>
      {(site.server || domain) && <hr className={"-mx-6 border-slate-100"} />}
      <div className={"flex flex-col xl:space-x-4 xl:flex-row"}>
        {site.server && <NavLink className={"mt-4 py-3 pr-4 border border-slate-100 rounded-sm w-full flex items-center justify-between hover:bg-slate-50"} to={`/servers/${site.serverId}`}>
          <div className={"w-8 h-8 mx-4 rounded-full bg-brand-100 flex items-center justify-center"}>
            <FontAwesomeIcon icon={faServer} className={"text-brand-500"} />
          </div>
          <dl className={"flex flex-col items-start flex-1"}>
            <dt className={"text-slate-500 text-xs"}>Server</dt>
            <dd className={"text-slate-900"}>{site.server.displayName}</dd>
          </dl>
          <div className={"text-brand-600 flex items-center justify-end space-x-3"}>
            <div className={"text-xs uppercase tracking-wide font-medium"}>Details</div> <FontAwesomeIcon icon={faChevronRight} />
          </div>
        </NavLink>}
        {domain && <NavLink className={"mt-4 py-3 pr-4 border border-slate-100 rounded-sm w-full flex items-center justify-between hover:bg-slate-50"} to={`/domains/${domain?.id}`}>
          <div className={"w-8 h-8 mx-4 rounded-full bg-brand-100 flex items-center justify-center"}>
            <FontAwesomeIcon icon={faGlobe} className={"text-brand-500"} />
          </div>
          <dl className={"flex flex-col items-start flex-1"}>
            <dt className={"text-slate-500 text-xs"}>Domain & DNS</dt>
            <dd className={"text-slate-900"}>{domain.displayName}</dd>
          </dl>
          <div className={"text-brand-600 flex items-center justify-end space-x-3"}>
            <div className={"text-xs uppercase tracking-wide font-medium"}>Details</div> <FontAwesomeIcon icon={faChevronRight} />
          </div>
        </NavLink>}
      </div>
    </Card>
  </>
}

const OptimizeOptions: FC<{ site: Site }> = ({ site }) => {
  const { partnerClient } = useApiClient(config)
  const { resource: optimizations, loading: loadingOptimizations, reload: reloadOptimizations } = useFetchedResource(() => partnerClient.getSiteOptimizeReport(site.id))
  useEffect(() => {
    reloadOptimizations()
  }, [])

  const canLaunch = optimizations?.success && !loadingOptimizations && optimizations.data.canSetLive && !optimizations.data.isLive
  const canOptimize = optimizations?.success && !loadingOptimizations && optimizations.data.canBeOptimized && optimizations.data.isLive && !optimizations.data.isOptimized
  const isOptimized = optimizations?.success && !loadingOptimizations && optimizations.data.isOptimized

  const launch = async () => {
    await partnerClient.optimizeSite(site.id, 'only_live')
    await reloadOptimizations()
  }
  const optimize = async () => {
    await partnerClient.optimizeSite(site.id, 'live_and_optimize')
    await reloadOptimizations()
  }

  return <>
    {loadingOptimizations && <div className={"h-24 px-6 flex my-5 items-center rounded-lg bg-linear-to-b from-slate-100 to-slate-100 text-white border-2 border-slate-50 animate-pulse"}></div>}
    {canLaunch && <div className={"h-24 px-6 flex my-5 items-center rounded-lg bg-linear-to-b from-brand-600 to-brand-700 text-white border-2 border-brand-300"}>
      <div className={"flex-1"}>
        <h2 className={"font-medium text-lg"}>Launch your site</h2>
        <p className={"text-sm"}>
          This will update the DNS records in your zone to point to the server where this site is hosted.
        </p>
      </div>
      <div className={"flex items-center"}>
        <Button icon={faRocket} type={"white"} size={"sm"} text={"Launch"} onClick={launch} />
      </div>
    </div>}
    {canOptimize && <div className={"h-24 px-6 flex my-5 items-center rounded-lg bg-white border border-slate-100"}>
      <div className={"flex-1"}>
        <h2 className={"font-medium text-lg text-black"}>Apply optimized settings</h2>
        <p className={"text-sm text-slate-700"}>
          We will apply recommended security settings and enable DDoS mitigation.
        </p>
      </div>
      <div className={"flex items-center"}>
        <Button icon={faPlaneDeparture} type={"primary"} size={"sm"} text={"Optimize"} onClick={optimize} />
      </div>
    </div>}
    {isOptimized && <div className={"h-24 my-5 rounded-lg bg-green-500 border border-slate-100 wind"}>
      <div className={"flex-1 px-6 h-full rounded-lg flex items-center justify-between bg-linear-to-r from-transparent from-20% to-green-500"}>
        <div>
          <h2 className={"font-medium text-xl text-white -mb-1"}>Awesome 🎉</h2>
          <p className={"text text-green-50"}>
            Your site is fully optimized!
          </p>
        </div>
        <span className={"uppercase font-bold italic text-xl text-green-50 tracking-wider border border-green-400 px-2 rounded-sm"}>
          <FontAwesomeIcon icon={faBolt} className={'mr-2'} />
          Optimized
        </span>
      </div>
    </div>}
  </>
}

const AdvancedSiteSettings: FC<{ site: Site }> = props => {
  const details = props.site.deploymentDetails
  const { partnerClient } = useApiClient(config)
  const { can } = usePermissions(partnerClient)
  const nginxModal = useModal({ title: 'Edit Nginx config', size: 'lg', body: <EditSiteNginxConfigModal entity={props.site} /> })
  const phpModal = useModal({ title: 'Edit PHP-FPM config', size: 'lg', body: <EditSitePhpConfigModal entity={props.site} /> })
  const deleteModal = useModal({ title: "Delete site", size: 'md', body: <DeleteSiteModal entity={props.site} /> })
  if (!details) {
    return <></>
  }
  return <Card title={'Advanced'} label={"beta"}>
    <div className={'flex items-center space-x-4'}>
      <div
        className={'h-24 w-24 border border-brand-300 bg-brand-50 rounded-full flex flex-col items-center justify-center'}>
        <span className={"text-xs text-brand-900"}>Storage</span>
        <span className={'text-brand-900 font-medium'}>{createStorageString(details.storageUsage)}</span>
      </div>
      <div
        className={'h-24 w-24 border border-brand-300 bg-brand-50 rounded-full flex flex-col items-center justify-center'}>
        <span className={"text-xs text-brand-900"}>Database</span>
        <span className={'text-brand-900 font-medium'}>{createStorageString(details.databaseUsage)}</span>
      </div>
      <div
        className={'h-24 w-24 border border-brand-300 bg-brand-50 rounded-full flex flex-col items-center justify-center'}>
        <span className={"text-xs text-brand-900"}><HoverHint hint={'measured per calendar month'}>Traffic <FontAwesomeIcon icon={faInfoCircle} className={'opacity-50'} /></HoverHint></span>
        <span className={'text-brand-900 font-medium'}>{createStorageString(details.trafficUsage)}</span>
      </div>
    </div>
    {can('edit', 'sites', props.site.permissions) && <>
      <hr className={'-mx-6 my-4 border-slate-100'} />
      <div className={"flex items-center"}>
        <div className={"h-12 w-12 rounded-full bg-brand-50 flex items-center justify-center"}>
          <FontAwesomeIcon icon={faCode} className={"text-brand-500"} />
        </div>
        <div className={"flex-1 mx-6"}>
          <h2 className={"font-medium text-lg"}>Nginx Config</h2>
          <p className={"text-slate-800 text-sm max-w-lg"}>
            Make custom changes to the <strong>nginx</strong> config, beware for breaking changes.
          </p>
        </div>
        <Button type={'secondary'} icon={faCog} size={'sm'} text={'Custom config'} onClick={() => nginxModal.open()} />
      </div>
      <hr className={'-mx-6 my-4 border-slate-100'} />

      <div className={"flex items-center"}>
        <div className={"h-12 w-12 rounded-full bg-brand-50 flex items-center justify-center"}>
          <FontAwesomeIcon icon={faCode} className={"text-brand-500"} />
        </div>
        <div className={"flex-1 mx-6"}>
          <h2 className={"font-medium text-lg"}>PHP-FPM Config</h2>
          <p className={"text-slate-800 text-sm max-w-lg"}>
            Make custom changes to the <strong>php-fpm</strong> config, beware for breaking changes.
          </p>
        </div>
        <Button type={'secondary'} icon={faCog} size={'sm'} text={'Custom config'} onClick={() => phpModal.open()} />
      </div>
      <hr className={'-mx-6 my-6 border-slate-100'} />
      <div className={'flex items-center'}>
        <div className={'h-12 w-12 rounded-full bg-brand-50 flex items-center justify-center'}>
          <FontAwesomeIcon icon={faTrash} className={'text-brand-500'} />
        </div>
        <div className={'flex-1 mx-6'}>
          <h2 className={'font-medium text-lg'}>Delete site</h2>
          <p className={"text-slate-800 text-sm max-w-lg"}>
            Allows a date to be entered for when the Webpage should be deleted, after a contract ends.
          </p>
        </div>
        {props.site.removeAt != null ?
          <div className={"py-2 px-2 text-red-700 rounded-sm flex space-x-2 content-center justify-center items-center"}>
            <span className={"italic"}>Deletion pending...</span>
          </div>
          :
          <Button type={'danger'} size={'sm'} text={'Delete'} icon={faTrash} onClick={() => deleteModal.open()} />
        }
      </div>
    </>}
  </Card>
}

const SshKeyCard: FC<{ keys: SshKey[], site: Site }> = props => {
  const { partnerClient } = useApiClient(config)
  const [addedKeys, setAddedKeys] = useState<SshKey[]>([])
  const [removedKeys, setRemovedKeys] = useState<SshKey[]>([])
  const addKey = useCallback(async (key: SshKey) => {
    setAddedKeys(a => [...a, key])
    setRemovedKeys(r => r.filter(k => k.id !== key.id))
    partnerClient.addSshKeyToSite(props.site.id, key.id)
  }, [])
  const removeKey = useCallback(async (key: SshKey) => {
    setRemovedKeys(a => [...a, key])
    setAddedKeys(r => r.filter(k => k.id !== key.id))
    partnerClient.deleteSshKeyFromSite(props.site.id, key.id)
  }, [])

  const keys = Array.from(new Set([...props.keys, ...addedKeys].filter(k => !removedKeys.map(k => k.id).includes(k.id))))
  const { open: openAddDeploymentKeyModal } = useModal({
    title: 'Add new SSH key',
    body: <AddDeploymentKeyModal filterKeys={(k) => !keys.map(k => k.id).includes(k.id)}
      onKeySelected={(depKey) => addKey(depKey)} />
  })
  const { can } = usePermissions(partnerClient)
  return <Card title={"SSH Keys with access"}>

    {keys.length === 0 && <Paragraph>No keys have access to this site</Paragraph>}
    <ul className={"flex flex-wrap gap-4 mb-6"}>
      {keys.map(key => {
        return <li key={key.id}>
          <div
            className={`relative flex justify-start items-center h-12 min-w-62.5 text-sm rounded-lg font-medium px-4 py-3 border bg-slate-100 text-slate-900 border-transparent`}>
            <FontAwesomeIcon icon={faKey} className={"mr-2"} />
            <div className={"flex-1 text-left mx-2"}>
              {key.displayName}
            </div>
            {can('edit', 'sites', props.site.permissions) && <button className={"h-6 w-6 border border-slate-300 rounded-sm text-slate-600 hover:bg-slate-200 hover:text-red-600 text-xs"} onClick={() => { removeKey(key) }}>
              <FontAwesomeIcon icon={faChainSlash} />
            </button>}
          </div>
        </li>
      })}
    </ul>
    {can('edit', 'sites', props.site.permissions) && <Button type={"secondary"} size={"sm"} text={"Add key"} icon={faAdd} onClick={() => { openAddDeploymentKeyModal() }} />}
  </Card>
}

const MonitoringCard: FC = () => {
  return <Card title={'Health & Uptime'}>
    <div className={"flex flex-col lg:flex-row items-stretch"}>
      <div className={"flex-1 space-y-4"}>

        <div className={"space-x-4 flex items-center text-slate-700"}>
          <div className={"w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center"}>
            <FontAwesomeIcon icon={faHeartCircleCheck} className={"text-emerald-500"} />
          </div>
          <strong>DNS is configured correctly</strong>
        </div>

        <div className={"space-x-4 flex items-center text-slate-700"}>
          <div className={"w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center"}>
            <FontAwesomeIcon icon={faHeartCircleCheck} className={"text-emerald-500"} />
          </div>
          <strong>SSL Certificate is valid</strong>
        </div>

        <div className={"space-x-4 flex items-center text-slate-700"}>
          <div className={"w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center"}>
            <FontAwesomeIcon icon={faHeartCircleCheck} className={"text-emerald-500"} />
          </div>
          <strong>Site has been online for 29 days</strong>
        </div>

      </div>
      <div className={"lg:w-96 py-4 lg:py-0 mt-4 lg:mt-0 bg-slate-50 rounded-lg flex flex-col items-center justify-center text-slate-500"}>
        <div className={"text-sm"}>Average response time graph</div>
      </div>
    </div>
  </Card>
}

function createStorageString(storage: number) {
  if (storage > 1024 ** 8) {
    return `${Math.round(storage / 1024 ** 8)} YB`
  }
  if (storage > 1024 ** 7) {
    return `${Math.round(storage / 1024 ** 7)} ZB`
  }
  if (storage > 1024 ** 6) {
    return `${Math.round(storage / 1024 ** 6)} EB`
  }
  if (storage > 1024 ** 5) {
    return `${Math.round(storage / 1024 ** 5)} PB`
  }
  if (storage > 1024 ** 4) {
    return `${Math.round(storage / 1024 ** 5)} TB`
  }
  if (storage > 1024 ** 3) {
    return `${Math.round(storage / 1024 ** 3)} GB`
  }
  if (storage > 1024 ** 2) {
    return `${Math.round(storage / 1024 ** 2)} MB`
  }
  if (storage > 1024) {
    return `${Math.round(storage / 1024)} KB`
  }
  return `${storage} B`
}
